import { Edit3 } from 'lucide-react';
import { X } from 'lucide-react';

import { RefreshToken } from '@crossnokaye/typescript-sdk/models/components';
import {
  Button,
  cn,
  DrawerHeader,
  DrawerTitle,
  FormControl,
  FormField,
  FormItem,
  HeaderWithIcon,
  Input,
  Label,
  Separator,
  Textarea,
} from '@crossnokaye/ux-common';
import { Drawer, DrawerContent } from '@crossnokaye/ux-common';

import { EditTokenActions } from '@/features/tokens/components/edit-token-drawer/edit-token-actions';
import { EditTokenForm } from '@/features/tokens/components/edit-token-drawer/edit-token-form';

interface EditTokenDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  token: RefreshToken;
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const EditTokenDrawer = ({ isOpen, onClose, token }: EditTokenDrawerProps) => {
  return (
    <Drawer
      container={document.getElementById('base-container')}
      direction={'right'}
      dismissible={true}
      open={isOpen}
      onOpenChange={onClose}
    >
      <DrawerContent
        className={cn(
          'right-0 top-0 bottom-0 flex mt-0 left-auto rounded-none border-none overflow-y-auto bg-secondary absolute w-full md:w-[60%] lg:w-[45%] xl:w-[40%] isolate'
        )}
        aria-describedby={undefined}
      >
        <div className={'flex flex-col h-full w-full'}>
          <DrawerHeader className={'p-0'}>
            <DrawerTitle>
              <HeaderWithIcon
                icon={<Edit3 size={26} />}
                subtitle={token.name ?? ''}
                title={'Edit Token'}
              />
            </DrawerTitle>

            <Button
              size={'icon'}
              variant={'secondary'}
              onClick={onClose}
              className={'absolute right-2 top-2'}
            >
              <X className={'w-4 h-4'} />
            </Button>
          </DrawerHeader>

          <div className={'flex-grow py-8 px-12'}>
            <div className={'space-y-12'}>
              <div className={'pt-4'}>
                <h2 className={'text-xl font-semibold mb-6'}>{'Token Information'}</h2>
                <div className={'space-y-6 mb-8'}>
                  <div className={'grid grid-cols-2 gap-8'}>
                    <div>
                      <Label className={'text-sm text-muted-foreground'}>{'Created'}</Label>
                      <p className={'mt-1.5'}>
                        {token.createdAt ? formatDate(token.createdAt.toISOString()) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <Label className={'text-sm text-muted-foreground'}>{'Expires'}</Label>
                      <p className={'mt-1.5'}>
                        {token.expiresAt ? formatDate(token.expiresAt.toISOString()) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <EditTokenForm token={token} onSuccess={onClose}>
                    {(form) => (
                      <div className={'space-y-4'}>
                        <FormField
                          control={form.control}
                          name={'name'}
                          render={({ field }) => (
                            <FormItem className={'space-y-2'}>
                              <Label htmlFor={'name'}>{'Name'}</Label>
                              <FormControl>
                                <Input
                                  id={'name'}
                                  placeholder={'e.g., Development API Token'}
                                  {...field}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={'description'}
                          render={({ field }) => (
                            <FormItem className={'space-y-2'}>
                              <Label htmlFor={'description'}>{'Description (optional)'}</Label>
                              <FormControl>
                                <Textarea
                                  id={'description'}
                                  placeholder={'Enter a description for this token'}
                                  className={'resize-none'}
                                  {...field}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                  </EditTokenForm>
                </div>
              </div>

              <Separator />

              <EditTokenActions token={token} closeDrawer={onClose} />
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
